import { useQuery, useLazyQuery, useMutation } from '@apollo/react-hooks';
import {
  Button,
  makeStyles,
  Box,
  Grid,
  Typography,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@material-ui/core';
import React, { useEffect } from 'react';
import DetailedTable from '../../components/DetailedTable';
import {
  PARTICIPANT_SUMMARY,
  USER_DETAILS,
  SUBSCRIBED_SERVICES,
  RESET_CREDENTIALS,
  WORKFLOW_QUERY,
  GET_CONFIGURATION_SETTINGS
} from '../graphQL/queries';
import columns from './columns';
import getParticipantSupportColumns from './supportParticipantColumns';
import Edit from '@jda/lui-common-icon-library/ui/Edit';
import Lock from '@jda/lui-common-icon-library/ui/Lock';
import { Loading } from '../../Loading';
import { Filter, FilterOff } from '@jda/lui-common-icon-library';
import { DispatchContext } from '../../App';
import AddParticipantDialog from './AddParticipantDialog';
import ParticipantFilterExpansionPanel from './ParticipantFilterExpansion';
import _ from 'lodash';
import ViewDetails from '@jda/lui-common-icon-library/ui/ViewDetails';
import toolTipInfo from '../../utils/tooltipInfo.json';
import { DialogForm } from '../../components/DialogForm';
import { useSnackbar } from 'notistack';
import { ClientPrimaryCredsConfig } from './formDataConfig';

const searchColumns = ['participantCode'];

const useStyles = makeStyles(theme => ({
  highlight: {
    color: '#047ABC',
    '& > *': {
      fill: '#047ABC'
    }
  }
}));
const participantTypeData = [
  {
    id: 1,
    value: 'ALL'
  },
  {
    id: 2,
    value: 'CUSTOMER'
  },
  {
    id: 3,
    value: 'PROVIDER'
  },
  {
    id: 4,
    value: 'AGGREGATOR'
  },
  {
    id: 5,
    value: 'CUSTOMER_AGGREGATOR'
  }
];

const filterDataConfig = [
  {
    label: 'Participant Type',
    id: 'participantType',
    type: 'multiSelect',
    placeholder: 'Participant Type'
  },
  {
    label: 'Participant Code',
    id: 'participantCode',
    type: 'search',
    placeholder: 'Participant Code'
  },
  {
    label: 'Participant Name',
    id: 'companyName',
    type: 'search',
    placeholder: 'Participant Name'
  },
  {
    label: 'Subscribed Service',
    id: 'workflowCode',
    type: 'select',
    placeholder: 'Subscribed Service'
  }
];

type specObj = {
  participantCode: string;
  companyName: string;
  primaryEmail: string;
  status: string;
  workflowsCount: number;
  providerCount: number;
  customerCount: number;
};

type responseObj = {
  participantInfo: contentData;
};
type contentData = {
  content: specObj[];
};

const ParticipantGrid = () => {
  const classes = useStyles();
  let defaultFilter: any = {
    participantType: '',
    participantCode: [],
    companyName: [],
    workflowCode: { service: null }
  };
  const { data: workflowList } = useQuery(WORKFLOW_QUERY, {});
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = React.useState({ ...defaultFilter });
  const [openFilter, setOpenFilter] = React.useState(false);
  const [appliedFilter, setAppliedFilter] = React.useState(false);
  const dispatch = React.useContext(DispatchContext);

  const { loading: userLoading, data: userData } = useQuery(USER_DETAILS, { fetchPolicy: 'cache-only' });
  const { loading, data, refetch } = useQuery<responseObj, {}>(PARTICIPANT_SUMMARY, {
    skip:
      userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
      userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN'
  });
  // const { loading: configurationLoading, data: configurationData } = useQuery(GET_CONFIGURATION_SETTINGS, {
  //   variables: { applicationId: 'BYN-SelfService', keys: [] }
  // });

  const [addParticipant, setAddParticipant] = React.useState(false);
  const [isActionOnSelect, showActionsOnToolbarSelect] = React.useState(false);
  const [formInfo, setFormInfo] = React.useState<any>({});
  const [formData, setFormData] = React.useState<any>({});
  const [edit, isEdit] = React.useState<any>(false);
  const [isFilterApplied, setIsFilterApplied] = React.useState(false);
  const [inputText, setInputText] = React.useState('');
  const [userRole, setUserRole] = React.useState('');
  const [subscribedWorkflowCode, SetSubscribedWorkflowCode] = React.useState('');
  const [participantData, setParticipantData] = React.useState<any>([]);
  const [resetPopup, setResetPopup] = React.useState<any>(false);
  const [showCreds, setShowCreds] = React.useState<any>(false);
  const [showWarning, setShowWarning] = React.useState<any>(false);
  const [clientPrimaryCreds, setClientPrimaryCreds] = React.useState<any>({});
  const [getSubscribedServices, { loading: subscribedServiceLoading, data: subscribedServiceData }] = useLazyQuery(
    SUBSCRIBED_SERVICES,
    {
      variables: { workflowCode: subscribedWorkflowCode },
      fetchPolicy: 'network-only'
    }
  );
  const [resetCredentials] = useMutation(RESET_CREDENTIALS);

  useEffect(() => {
    setParticipantData(data?.participantInfo?.content);
    setUserRole(userData?.userProfile?.roles?.[0]);
  }, [data, userData]);

  const onSearchChange = (searchKey: string) => {
    setInputText(searchKey);
    const participantResult = isFilterApplied ? filterData : data?.participantInfo?.content;
    const filteredData = participantResult?.filter((row: any) => {
      return String(searchColumns.map(column => String(row[column]).toLowerCase())).includes(searchKey.toLowerCase());
    });
    setParticipantData(filteredData);
  };

  const onClearText: any = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setInputText('');
    setParticipantData(data?.participantInfo?.content);
  };

  const participantTableActions: any = [
    userRole === 'APP_ADMIN' && {
      icon: () => (
        <Button
          variant="outlined"
          onClick={handleAddParticipantDialog}
          data-id="new-participant"
          id="new-participant"
          color="primary"
        >
          Add
        </Button>
      ),
      onClick: () => {},
      isFreeAction: !isActionOnSelect,
      position: isActionOnSelect ? 'toolbarOnSelect' : 'toolbar'
    },

    userRole !== 'APP_ADMIN' && userRole !== 'PARTICIPANT_ADMIN'
      ? {
          icon: () => <ViewDetails />,
          onClick: (event: any, rowData: any) => handleViewDetails(rowData),
          tooltip: 'View Details',
          position: 'row'
        }
      : {
          icon: () => <Edit data-id="edit-participant" />,
          onClick: (event: any, rowData: any) => handleEditParticipant(rowData)
        },
    (userRole === 'APP_ADMIN' || userRole === 'PARTICIPANT_ADMIN') && {
      icon: () => <Lock data-id="edit-participant" />,
      onClick: (event: any, rowData: any) => handleResetPopUp(rowData),
      position: 'row',
      tooltip: 'Reset Credentials'
    },

    {
      icon: () => <Filter className={appliedFilter ? classes.highlight : ''} />,
      isFreeAction: true,
      onClick: () => toggleFilter(),
      tooltip: 'Apply Filter'
    },
    {
      icon: () => <FilterOff />,
      isFreeAction: true,
      onClick: () => resetFilter(),
      tooltip: 'Reset Filter'
    }
  ];

  const handleResetPopUp = (rowData: any) => {
    setResetPopup(!resetPopup);
    setFormData(rowData);
  };

  const handleCloseResetPopup = () => {
    setResetPopup(!resetPopup);
    setFormData({});
  };

  const applyFilter = (values: any) => {
    setIsFilterApplied(true);

    getSubscribedServices();

    dispatch({
      type: 'userPreferences',
      data: {
        participantGrid: {
          id: 'participantGrid',
          rowsPerPage: 10,
          page: 0
        }
      }
    });
    const isDefaultCriteria: boolean = _.isEqual(values, defaultFilter);
    if (!_.isEqual(values, search) && !isDefaultCriteria) {
      setAppliedFilter(true);
    } else if (isDefaultCriteria) {
      setAppliedFilter(false);
    }
    setSearch({ ...values });
  };

  const resetFilter = () => {
    dispatch({
      type: 'userPreferences',
      data: {
        participantGrid: {
          id: 'participantGrid',
          rowsPerPage: 10,
          page: 0
        }
      }
    });
    setSearch({ ...defaultFilter });
    setAppliedFilter(false);
    setIsFilterApplied(false);
  };

  const toggleFilter = () => {
    setOpenFilter(!openFilter);
  };

  const isPresent = (key: string) => {
    return search && search[key] && search[key].length > 0;
  };

  const toColonSperated = (key: string) => {
    return search[key].map((val: string) => val.trim()).join(';');
  };

  let filterCriteria: any = {};
  filterCriteria.participantType = search['participantType']?.participantType;
  filterCriteria.workflowCode = search['workflowCode']?.service;
  if (isPresent('participantCode')) {
    filterCriteria.participantCode = toColonSperated('participantCode');
  }

  if (isPresent('companyName')) {
    filterCriteria.participantName = toColonSperated('companyName');
  }

  // if (isPresent('service')) {
  //   filterCriteria.service = toColonSperated('service');
  // }
  // if (isPresent('workflowCode')) {
  //   filterCriteria.workflowCode = toColonSperated('workflowCode');
  // }

  // if (filterCriteria?.service?.service) {
  //   let serviceArray = filterCriteria?.service?.service;
  //   search.service = serviceArray.join(':');
  // }

  useEffect(() => {
    SetSubscribedWorkflowCode(filterCriteria.workflowCode);
  }, [filterCriteria]);
  let filterData: any = [];

  (data?.participantInfo?.content || []).forEach((arr: any) => {
    if (
      filterCriteria.participantType?.toString() === 'ALL' &&
      (arr.participantType?.toString() === 'CUSTOMER' ||
        arr.participantType?.toString() === 'PROVIDER' ||
        arr.participantType?.toString() === 'AGGREGATOR' ||
        arr.participantType?.toString() === 'CUSTOMER_AGGREGATOR') &&
      (filterCriteria.participantCode !== undefined ? arr.participantCode === filterCriteria.participantCode : true) &&
      (filterCriteria.companyName !== undefined ? arr.companyName === filterCriteria.companyName : true)
    ) {
      if (filterCriteria.workflowCode) {
        (subscribedServiceData?.subscribedService?.content || []).forEach((sub: any) => {
          if (arr.participantCode === sub.participantCode) {
            filterData.push(arr);
          }
        });
      } else {
        filterData.push(arr);
      }
    } else if (
      filterCriteria.participantType?.toString() !== 'ALL' &&
      arr.participantType?.toString() === filterCriteria.participantType?.toString() &&
      (filterCriteria.participantCode !== undefined ? arr.participantCode === filterCriteria.participantCode : true) &&
      (filterCriteria.companyName !== undefined ? arr.companyName === filterCriteria.companyName : true)
    ) {
      if (filterCriteria.workflowCode) {
        (subscribedServiceData?.subscribedService?.content || []).forEach((sub: any) => {
          if (arr.participantCode?.toString() === sub.participantCode?.toString()) {
            filterData.push(arr);
          }
        });
      } else {
        filterData.push(arr);
      }
    }
  });

  // (data?.participantInfo?.content || []).forEach((arr: any) => {
  //   console.log('Filter Ptype: '+filterCriteria.participantType?.[0] );
  //   // if (
  //   //   filterCriteria.participantType?.[0] === 'ALL' &&
  //   //   (arr.participantType === 'CUSTOMER' ||
  //   //     arr.participantType === 'PROVIDER' ||
  //   //     arr.participantType === 'AGGREGATOR' ||
  //   //     arr.participantType === 'CUSTOMER_AGGREGATOR') &&
  //   //   (filterCriteria.participantCode !== undefined ? arr.participantCode === filterCriteria.participantCode : true) &&
  //   //   (filterCriteria.companyName !== undefined ? arr.companyName === filterCriteria.companyName : true)
  //   // ) {
  //   //   console.log('All All All All'+filterCriteria.participantType?.[0] );
  //   //   if (filterCriteria.workflowCode.service) {
  //   //     (subscribedServiceData?.subscribedService?.content || []).forEach((sub: any) => {
  //   //       if (arr.participantCode === sub.participantCode) {
  //   //         filterData.push(arr);
  //   //       }
  //   //     });
  //   //   } else {
  //   //     filterData.push(arr);
  //   //   }
  //   // } else
  //   if (
  //     (arr.participantType === filterCriteria.participantType?.[1] ||
  //       arr.participantType === filterCriteria.participantType?.[2] ||
  //       arr.participantType === filterCriteria.participantType?.[3] ||
  //       arr.participantType === filterCriteria.participantType?.[4]) &&
  //     (filterCriteria.participantCode !== undefined ? arr.participantCode === filterCriteria.participantCode : true) &&
  //     (filterCriteria.companyName !== undefined ? arr.companyName === filterCriteria.companyName : true)
  //   ) {
  //     console.log('Customer');
  //     if (filterCriteria.workflowCode.service) {
  //       (subscribedServiceData?.subscribedService?.content || []).forEach((sub: any) => {
  //         if (arr.participantCode === sub.participantCode) {
  //           filterData.push(arr);
  //         }
  //       });
  //     } else {
  //       filterData.push(arr);
  //     }
  //   }
  // });

  const handleAddParticipantDialog = () => {
    setFormInfo({});
    setFormData({ participantType: 'CUSTOMER', isAdditionalParametersRequired: 'no' });
    isEdit(false);
    setAddParticipant(true);
  };

  const handleAddParticipantDialogClose = () => {
    setFormInfo({});
    setAddParticipant(false);
  };

  const handleAddParticipantDialogSubmit = (formData: any) => {
    setAddParticipant(false);
  };

  const onSelectionAction = (data: Array<any>) => {
    if (data.length === 0) {
      showActionsOnToolbarSelect(false);
    } else {
      showActionsOnToolbarSelect(true);
    }
  };

  const handleEditParticipant = (rowData: any) => {
    setFormData(rowData);
    isEdit(true);
    setAddParticipant(true);
  };

  const handleViewDetails = (rowData: any) => {
    setFormData(rowData);
    setAddParticipant(true);
  };

  const handleResetSubmit = () => {
    if (formData) {
      resetCredentials({ variables: { participantCode: formData?.participantCode } })
        .then((response: any) => {
          enqueueSnackbar('User Credential reset successfully !!', { variant: 'success', preventDuplicate: true });
          handleCloseResetPopup();
          setShowCreds(!showCreds);
          setClientPrimaryCreds(response?.data?.postResetCredsData?.clientCredentials || {});
          setShowWarning(!showWarning);
          return response;
        })
        .catch(error => {
          enqueueSnackbar('Unable to delete User', { variant: 'error', preventDuplicate: true });
        });
    }
  };

  const showAdditionalWarning = () => {
    setShowWarning(!showWarning);
  };
  return (
    <div>
      <Grid container spacing={1} direction="row">
        <Grid item xs={openFilter ? 9 : 12} md={openFilter ? 9 : 12}>
          <Box style={{ borderColor: '#DCDCDC' }} borderRight={1}>
            {(userData?.userProfile?.roles?.[0] !== 'PARTICIPANT_SUPPORT' ||
              userData?.userProfile?.roles?.[0] !== 'PARTICIPANT_ADMIN') && (
              <AddParticipantDialog
                edit={edit}
                dialogOpen={addParticipant}
                formInfo={formInfo}
                refresh={refetch}
                formData={formData}
                handleDialogClose={handleAddParticipantDialogClose}
                handleDialogSubmit={handleAddParticipantDialogSubmit}
                supportUser={userRole !== 'APP_ADMIN' ? true : false}
              />
            )}
            {loading || userLoading ? (
              <Loading />
            ) : (
              <DetailedTable
                key="participantInfo"
                id="participantGrid"
                title={'Participants'}
                columns={
                  userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
                  userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN'
                    ? getParticipantSupportColumns
                    : columns
                }
                data={
                  isFilterApplied && inputText === ''
                    ? filterData
                    : userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
                      userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN'
                    ? userData?.userProfile?.participantRoleInfos
                    : participantData
                }
                showSelection={false}
                showPagination={true}
                showSpecificPage={true}
                showTopPagination={true}
                showSearch={true}
                page={0}
                rowsPerPage={20}
                tableActions={
                  userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ? [] : participantTableActions
                }
                onSelection={onSelectionAction}
                labelRowsPerPage="Participants Per Page"
                inputText={inputText}
                onClearText={onClearText}
                onSearchChange={onSearchChange}
                pageLevelInfo={toolTipInfo?.pages?.participant}
              />
            )}
          </Box>
        </Grid>
        {openFilter && (
          <Grid item xs={3} md={3}>
            {
              <ParticipantFilterExpansionPanel
                key="loads-filter"
                data-id="loads-filter"
                onSubmit={applyFilter}
                onReset={resetFilter}
                defaultFilter={search}
                filterDataConfig={filterDataConfig}
                closeFilter={() => setOpenFilter(false)}
                participantTypeData={participantTypeData}
                workflowList={workflowList?.workflow?.content || []}
              />
            }
          </Grid>
        )}
        <DialogForm
          openFn={resetPopup}
          dataConfig={[]}
          handleDialogClose={handleCloseResetPopup}
          handleDialogSubmit={showWarning ? handleResetSubmit : showAdditionalWarning}
          submitlabel="Yes"
          title="Reset Credentials"
          dialogId="reset-credentials-id"
          closeFn={handleCloseResetPopup}
          maxWidth="sm"
        >
          <Typography color="textPrimary">
            {showWarning
              ? 'Are you sure that you want to reset the credentials? Please note that resetting the credentials will make all the existing integrations invalid.'
              : ' Resetting the credentials makes the old credentials invalid. Would you like to proceed ?'}
          </Typography>
        </DialogForm>
        <Dialog open={showCreds} aria-labelledby="customized-dialog-title">
          <DialogContent>
            <DialogContentText>
              {clientPrimaryCreds ? (
                <div>
                  <Typography>Reset Credentials is Successfully updated with</Typography>
                  {(ClientPrimaryCredsConfig || []).map((obj: any) => (
                    <Grid container>
                      <Grid item>
                        <Typography>{`${obj.label}-${clientPrimaryCreds[obj.value]}`}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                </div>
              ) : (
                <Typography>Client Credentials are not available</Typography>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button color="primary" autoFocus variant="contained" onClick={() => setShowCreds(!showCreds)}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </div>
  );
};
export default ParticipantGrid;
